import axios from "axios";
import React from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { setAuthData } from "./redux/auth";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AuthHelper from "./utils/AuthHelper";
import Header from "./Header";

const Card = styled(Box)(() => ({
  width: "100%",
  borderRadius: 16,
  overflow: "hidden",
  backgroundColor: "#FFFFFF",
  border: "1px solid rgba(0,0,0,0.08)",
}));

const BrandContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  maxHeight: 420,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "#E0E0E0 transparent",
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "#E0E0E0", borderRadius: 4 },
});

type Client = {
  id: string;
  tenant_name: string;
  domain_name: string;
  tenant_type: string;
  logo: string;
};

type AppData = {
  clientsData: Client[];
  domainName: { [key: string]: string };
};

function Clients() {
  const [searchParams] = useSearchParams();
  const requestCode = searchParams.get("code");
  const dispatch = useDispatch();
  const appData = useSelector((state: { auth: AppData }) => state.auth);

  const authHelper = new AuthHelper();
  const userData = authHelper.decodeTokenId();

  const [isLoading, setIsLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    fetchAuthTokenAndClientData();
    // eslint-disable-next-line
  }, [requestCode]);

  const fetchAuthTokenAndClientData = async () => {
    try {
      setIsLoading(true);
      let clients: string[] = [];
      if (userData["custom:clients"]) {
        clients = userData["custom:clients"].split(",");
      }
      const clientsData = await fetchClientsData(clients);
      dispatch(setAuthData({ clientsData }));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientsData = async (clients: string[]): Promise<Client[]> => {
    const requestEndpoints = clients
      .filter((client) => client !== "")
      .map((client) => {
        const clientArr = client.split("_");
        return `${process.env.REACT_APP_WMS_ENDPOINT_V1}tenant/${clientArr.pop()}`;
      });
    const requestPromise = requestEndpoints.map((endpoint) => axios.get(endpoint));
    const responseData = await Promise.all(requestPromise);
    return responseData.flatMap(({ data }) => data);
  };

  const onClientSelectHandler = (client: Client) => {
    const { domainName } = appData;
    const searchParams = {
      tenant: `tenant_${client.id}`,
      tenantType: client.tenant_type,
      id_token: authHelper.getIdToken() || "",
      access_token: authHelper.getAccessToken() || "",
      username: (authHelper.decodeTokenId()?.phone_number || "").replace("+91", ""),
    };
    const queryParams = new URLSearchParams(searchParams);
    const domainUrl = domainName[`${process.env.REACT_APP_ENVIRONMENT}`];
    window.open(`${domainUrl}oauth?${queryParams.toString()}`, "_self");
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  let allClientList = appData?.clientsData || [];
  if (searchQuery) {
    allClientList = allClientList.filter((client) =>
      client.tenant_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  return (
    <Card>
      <Header />

      {/* Search */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          size="small"
          placeholder="Search by brand name"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: "#F5F7FA",
              borderRadius: "10px",
              fontFamily: "Poppins, sans-serif",
              fontSize: 13,
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "1px solid rgba(0,0,0,0.15) !important" },
            },
          }}
          inputProps={{ style: { fontFamily: "Poppins, sans-serif", fontSize: 13 } }}
        />
      </Box>

      {/* Empty state */}
      {allClientList.length === 0 && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <StorefrontIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
          <Typography sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: 14, color: "text.secondary" }}>
            No brands found
          </Typography>
          <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: 12, color: "text.disabled", mt: 0.5 }}>
            {searchQuery ? "Try a different search term" : "No brand mapped to your account"}
          </Typography>
        </Box>
      )}

      {/* Client list */}
      {allClientList.length > 0 && (
        <BrandContainer>
          <List disablePadding>
            {allClientList.map((client, index) => (
              <React.Fragment key={client.id}>
                <ListItem
                  onClick={() => onClientSelectHandler(client)}
                  alignItems="center"
                  sx={{
                    cursor: "pointer",
                    px: 2.5,
                    py: 1.5,
                    transition: "background 0.15s",
                    "&:hover": { bgcolor: "#F8FAFF" },
                    "&:hover .arrow-icon": { color: "#0B1B2E" },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      alt={client.tenant_name}
                      src={
                        client.logo ||
                        `${process.env.REACT_APP_WMS_CDN}tenant_${client.id}_logo-mini-md.png`
                      }
                      variant="rounded"
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        border: "1px solid rgba(0,0,0,0.08)",
                        "& img": { objectFit: "contain", padding: "4px", filter: "invert(1)" },
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: 14,
                      fontFamily: "Poppins, sans-serif",
                      color: "#0B1B2E",
                    }}
                    primary={client.tenant_name.replace(/([a-z])([A-Z])/g, "$1 $2")}
                  />
                  <KeyboardArrowRightIcon
                    className="arrow-icon"
                    sx={{ color: "text.disabled", fontSize: 20, transition: "color 0.15s", flexShrink: 0 }}
                  />
                </ListItem>
                {index < allClientList.length - 1 && (
                  <Divider component="li" sx={{ mx: 2.5, borderColor: "rgba(0,0,0,0.05)" }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </BrandContainer>
      )}
    </Card>
  );
}

export default Clients;
